import { Component, signal, inject, ChangeDetectionStrategy, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService } from '@services/auth.service';
import { NotificationService } from '@services/notification.service';
import { SidebarService } from '@services/sidebar.service';
import { NavigationService } from '@services/navigation.service';
import { NotificationsSidebarComponent } from './notifications-sidebar.component';
import { SettingsSidebarComponent } from './settings-sidebar.component';
import { type Notification } from './notification-item.component';

@Component({
  selector: 'app-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <header class="app-header">
      <div class="header-content">
        <!-- Menu Toggle Button -->
        <button
          class="menu-toggle-btn"
          (click)="sidebarService.toggle()"
          [attr.aria-label]="sidebarService.isExpanded() ? 'Cerrar menú' : 'Abrir menú'"
          type="button"
        >
          <i class="fas fa-bars"></i>
        </button>

        <!-- Logo Container -->
        <div class="logo-container">
          <svg
            class="yurest-logo"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1757 549"
            (click)="navigateToHome()"
            role="button"
            tabindex="0"
            (keydown.enter)="navigateToHome()"
            (keydown.space)="navigateToHome()"
            aria-label="Ir al inicio"
          >
            <path
              class="logo-path"
              d="M351.219,120.618v158.5c0,34.5-23,57.5-57.5,57.5-35,0-58.5-23-58.5-57.5v-158.5h-68v169c0,63.5,41.5,105.5,102.5,105.5,33,0,62-12.5,81.5-36v5c0,50-32.5,82.5-80.5,82.5-34.5,0-64-8.5-91-29l-15,53.5c27.5,22.5,65,34,107.5,34,88.5,0,147-55.5,147-140.5v-244h-68Zm234.5,274.5c75.5,0,125.5-45,125.5-112.5v-162h-68v158.5c0,34.5-23,57.5-57.5,57.5-35,0-58-23-58-57.5v-158.5h-68v162C459.716,350.118,509.716,395.118,585.716,395.118Zm208.5-225-0.5-44.5h-35v264.5h36v-153.5c0-50.5,37-83,101.5-83l3-33C848.711,120.618,813.211,137.118,794.211,170.118Zm340,84.5c0-80.5-50-134-124.5-134-75.495,0-125.495,55-125.495,137,0,82.5,51,137.5,126.5,137.5,47,0,87.5-20.5,113-58.5l-27.5-15.5c-20.5,27.5-50,45-85.5,45-52.495,0-83.495-37.5-87.995-95h211C1134.21,267.118,1134.21,259.118,1134.21,254.618Zm-124.5-105c51,0,84,37,87,96H922.715C926.215,186.618,958.715,149.618,1009.71,149.618Zm238,245.5c61,0,99.5-30.5,99.5-76.5,0-43.5-36.5-63.5-76.5-74.5l-28.5-8.5c-28.5-8-49-19-49-43.5,0-26,23.5-42.5,58.5-42.5,25.5,0,49.5,5.5,72,19.5l15.5-24.5c-25.5-16.5-54.5-24-90-24-56,0-93,30.5-93,77,0,39.5,28.5,56,70,67.5l28.5,8.5c32,9,55.5,23,55.5,48,0,27-25,44.5-62.5,44.5-27.5,0-54-5.5-76-21.5l-15,22.5C1177.21,385.618,1210.21,395.118,1247.71,395.118Zm219-31c-28,0-46.5-20-46.5-49v-159.5h95.5v-30h-95.5v-77.5h-36v274c0,43.5,28.5,73,71.5,73,24.5,0,46-3.5,64-11v-29.5A150.2,150.2,0,0,1,1466.71,364.118Z"
            />
            <path
              class="logo-path"
              d="M1535.04,71.249h2.39c2.77,0,4.1,1.059,4.49,3.441,0.67,2.514,1.06,4.5,1.72,5.293h5.43c-0.53-.794-0.93-2.117-1.59-5.425-0.66-2.911-1.85-4.5-3.97-5.293V69c2.65-.794,4.5-2.647,4.5-5.028a5.992,5.992,0,0,0-2.12-4.764c-1.58-.926-3.44-1.588-7.54-1.588a44.05,44.05,0,0,0-8.34.662v21.7h5.03V71.249Zm0.14-9.792a11.91,11.91,0,0,1,2.77-.265c3.18,0,4.64,1.323,4.64,3.308,0,2.249-2.25,3.176-5.03,3.176h-2.38V61.457Zm3.3-14.556c-12.43,0-22.36,9.66-22.36,21.7a22.3,22.3,0,0,0,44.59,0C1560.71,56.561,1551.05,46.9,1538.48,46.9Zm0,4.367c9.66,0,16.68,7.807,16.68,17.467,0,9.528-7.02,17.2-16.54,17.2-9.8,0-16.94-7.675-16.94-17.335C1521.68,59.075,1528.82,51.268,1538.48,51.268Z"
            />
          </svg>
        </div>

        <!-- Spacer -->
        <div class="header-spacer"></div>

        <!-- Settings Container -->
        <div class="settings-container">
          <button class="settings-btn" (click)="toggleSettingsSidebar()" aria-label="Configuración de usuario">
            <i class="fas fa-cog"></i>
          </button>
        </div>
      </div>
    </header>

    <!-- Notifications Sidebar -->
    <app-notifications-sidebar
      [isOpen]="showNotificationsSidebar()"
      [notifications]="notificationService.notifications()"
      (close)="closeNotificationsSidebar()"
      (notificationClick)="onNotificationClick($event)"
      (markAsRead)="onMarkAsRead($event)"
      (markAllAsRead)="onMarkAllAsRead()"
      (clearAll)="onClearAllNotifications()"
      (actionClick)="onNotificationAction($event)"
    >
    </app-notifications-sidebar>

    <!-- Settings Sidebar -->
    <app-settings-sidebar [isOpen]="showSettingsSidebar()" (close)="closeSettingsSidebar()" (logout)="logout()">
    </app-settings-sidebar>
  `,
  styles: [
    `
      .app-header {
        height: 56px;
        background: var(--bg-primary);
        border-bottom: 1px solid var(--border-primary);
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        z-index: 101;
      }

      .header-content {
        height: 100%;
        max-width: 100%;
        margin: 0 auto;
        padding: 0 var(--space-md);
        display: flex;
        align-items: center;
        gap: var(--space-sm);
      }

      /* Menu Toggle Button */
      .menu-toggle-btn {
        background: none;
        border: none;
        color: var(--text-muted);
        cursor: pointer;
        padding: var(--space-sm);
        border-radius: var(--radius-md);
        transition: all 0.2s ease;
        font-size: var(--text-lg);
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        flex-shrink: 0;
      }

      .menu-toggle-btn:hover {
        background: var(--bg-secondary);
        color: var(--text-primary);
      }

      .menu-toggle-btn:focus {
        outline: 2px solid var(--red-500);
        outline-offset: 2px;
      }

      /* Logo Container */
      .logo-container {
        flex-shrink: 0;
        display: flex;
        align-items: center;
      }

      .yurest-logo {
        height: 24px;
        width: auto;
        max-width: 100px;
        transition: all 0.2s ease;
        cursor: pointer;
      }

      .yurest-logo:focus {
        outline: 2px solid var(--red-400);
        outline-offset: 2px;
        border-radius: var(--radius-sm);
      }

      .logo-path {
        fill: var(--red-500);
        transition: fill 0.2s ease;
      }

      .yurest-logo:hover .logo-path,
      .yurest-logo:focus .logo-path {
        fill: var(--red-600);
      }

      /* Header Spacer */
      .header-spacer {
        flex: 1;
      }

      /* Settings Container */
      .settings-container {
        display: flex;
        align-items: center;
        gap: var(--space-xs);
        flex-shrink: 0;
      }

      .settings-btn {
        background: none;
        border: none;
        color: var(--text-muted);
        cursor: pointer;
        padding: var(--space-sm);
        border-radius: var(--radius-md);
        transition: all 0.2s ease;
        font-size: var(--text-lg);
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
      }

      .settings-btn:hover {
        background: var(--bg-secondary);
        color: var(--text-primary);
        transform: rotate(90deg);
      }

      .settings-btn:focus {
        outline: 2px solid var(--red-500);
        outline-offset: 2px;
      }

      /* Responsive Design */
      @media (max-width: 768px) {
        .header-content {
          padding: 0 var(--space-sm);
        }

        .yurest-logo {
          height: 20px;
          max-width: 80px;
        }

        .menu-toggle-btn,
        .settings-btn {
          width: 36px;
          height: 36px;
          font-size: var(--text-base);
        }
      }
    `,
  ],
  imports: [CommonModule, NotificationsSidebarComponent, SettingsSidebarComponent],
})
export class HeaderComponent {
  private navigationService = inject(NavigationService);
  authService = inject(AuthService);
  notificationService = inject(NotificationService);
  sidebarService = inject(SidebarService);

  // Settings state
  showSettingsSidebar = signal(false);

  // Notifications state
  showNotificationsSidebar = signal(false);

  // Computed properties
  unreadNotificationsCount = computed(() => {
    return this.notificationService.unreadCount();
  });

  constructor() {
    // Close settings sidebar when clicking outside
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const settingsSidebar = target.closest('.settings-sidebar, .settings-btn');
      if (!settingsSidebar && this.showSettingsSidebar()) {
        this.closeSettingsSidebar();
      }
    });

    // Close notifications sidebar when clicking outside
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const notificationsSidebar = target.closest('.notifications-sidebar, .notifications-btn');
      if (!notificationsSidebar && this.showNotificationsSidebar()) {
        this.closeNotificationsSidebar();
      }
    });
  }

  // Settings sidebar methods
  toggleSettingsSidebar(): void {
    this.showSettingsSidebar.update((current) => !current);
    this.closeNotificationsSidebar();
  }

  closeSettingsSidebar(): void {
    this.showSettingsSidebar.set(false);
  }

  // Navigation methods
  navigateToHome(): void {
    this.navigationService.navigate(['/']);
    this.closeSettingsSidebar();
  }

  // Notifications methods
  toggleNotificationsSidebar(): void {
    const isCurrentlyOpen = this.showNotificationsSidebar();
    this.showNotificationsSidebar.set(!isCurrentlyOpen);
    this.closeSettingsSidebar();
  }

  closeNotificationsSidebar(): void {
    this.showNotificationsSidebar.set(false);
  }

  onNotificationClick(notification: Notification): void {
    if (!notification.isRead) {
      this.onMarkAsRead(notification.id);
    }
    if (notification.actionUrl) {
      this.navigationService.navigate([notification.actionUrl]);
      this.closeNotificationsSidebar();
    }
  }

  onMarkAsRead(notificationId: string): void {
    this.notificationService.markAsRead(notificationId);
  }

  onMarkAllAsRead(): void {
    this.notificationService.markAllAsRead();
  }

  onClearAllNotifications(): void {
    this.notificationService.clearAllNotifications();
  }

  onNotificationAction(event: { notificationId: string; actionUrl: string }): void {
    this.navigationService.navigate([event.actionUrl]);
    this.closeNotificationsSidebar();
  }

  // Auth methods
  logout(): void {
    this.authService.logout();
    this.navigationService.navigate(['/login']);
    this.closeSettingsSidebar();
  }
}
